<template>
    <div class="page-content">
        <div class="d-flex align-items-center py-3 pl-4">
            <div>
                <div class="d-flex align-items-center mb-1">
                    <h1 class="text-nowrap mb-0 mr-2">{{ estimateName }}</h1>
                    <!-- (Optional) Add a label or icon here if needed -->
                </div>
                <SunbitStatusBadge
                    v-if="estimateDetails && estimateDetails.client"
                    :client-id="estimateDetails.client.id"
                    :collected-amount="numericTotalAmount"
                />
            </div>
        </div>

        <b-card footer-class="p-0" :key="cardKey">
            <div class="">
                <loader-profile
                    v-if="!isDataLoaded || isBusinessLoading"
                    :showImage="false"
                ></loader-profile>
                <div v-else>
                    <estimate-display
                        ref="invoiceDisplay"
                        v-if="profileMode === 'VIEW'"
                        :estimate="estimateDetails"
                    />
                    <estimate-edit
                        ref="estimateEdit"
                        v-if="profileMode === 'EDIT'"
                        :estimate="estimateDetails"
                        @saved="saved"
                        v-on="$listeners"
                    />
                </div>
            </div>

            <template v-slot:footer no-body v-if="estimateDetails">
                <b-tabs
                    card
                    lazy
                    class="list tab-table-responsive"
                    v-if="estimateDetails"
                    v-model="activeTabIndex"
                    ref="btabs"
                >
                    <tab-items
                        :key="1 + keyComponent"
                        ref="tabItems"
                        tabname="tabItems"
                        :estimate="estimateDetails"
                        @create-bundle="createBundle"
                        @reload-estimate-details="reloadPage"
                        @add-estimate-item="addEstimateItem"
                    />
                    <tab-declined
                        :key="2 + keyComponent"
                        ref="tabDeclined"
                        tabname="tabDeclined"
                        :estimate="estimateDetails"
                        @reload-estimate-details="reloadPage('tabItems')"
                    />
                </b-tabs>
            </template>
        </b-card>

        <div
            v-if="(estimateDetails && estimateDetails.client && estimateDetails.client.id)"
        >
            <popup-notes
                :clientId="estimateDetails.client.id"
                :key="popupKey"
            />
        </div>
        <modal-bundle
            ref="modalBundle"
            :isInvoice="false"
            @reload="reloadPage('tabItems')"
        />
        <estimate-item-modal
            v-if="estimateDetails"
            :isInvoice="false"
            ref="estimateItemModal"
            @reload="reloadPage('tabItems')"
        />
        <modal-switch-business
            ref="modalSwitchBusiness"
            @redirect-to-dashboard="redirectToDashboard"
        />
    </div>
</template>

<script>
/**
 * @name Estimate Details Page
 * @description estimate display, details and navbar
 * @see apps/application/views/staff/billing/invoice/estimate/sub/navbar.php
 *
 */

import unsavePrompt from "@/mixins/unsave-prompt.js";
import EstimateDisplay from "@/components/estimate/estimate-display";
import EstimateEdit from "@/components/estimate/estimate-update.vue";
import LoaderProfile from "@/components/elements/loader-profile";
import TabItems from "@/module/billing/tabs/estimate/tab-items";
import TabDeclined from "@/module/billing/tabs/estimate/tab-declined";
import PopupNotes from "@/components/client/popup-notes";
import CBILLINGS from "@/constants/billings";
import ModalBundle from "@/components/invoice-item/bundle-modal";
import estimateItemModal from "@/components/invoice-item/create-update";
import { isEmpty, findIndex } from "lodash";
import ModalSwitchBusiness from "@/components/commons/modal-switch-business.vue";
import SunbitStatusBadge from "@/components/payments/SunbitStatusBadge.vue";

export default {
    mixins: [unsavePrompt],
    components: {
        EstimateDisplay,
        EstimateEdit,
        LoaderProfile,
        TabItems,
        TabDeclined,
        PopupNotes,
        ModalBundle,
        estimateItemModal,
        ModalSwitchBusiness,
        SunbitStatusBadge
    },
    props: {
        profileMode: {
            type: String
        },
        invoiceDetails: {
            type: Object
        }
    },
    data() {
        return {
            isDataReloading: true,
            isLoading: false,
            record: {},
            keyComponent: 0,
            cardKey: 0,
            activeTabIndex: 0,
            firstTimeLoaded: false,
            isEditTypeFee: false
        };
    },
    computed: {
        estimateId() {
            return this.estimateDetails ? this.estimateDetails.id : null;
        },
        estimateDetails() {
            return this.$store.getters["billing/estimateDetails"];
        },
        estimateName() {
            if (!this.isDataLoaded) {
                return "Loading...";
            }
            return this.estimateDetails
                ? "Estimate #" + this.estimateDetails.index
                : "Estimate";
        },
        isDataLoaded() {
            return !isEmpty(this.estimateDetails) && !this.isDataReloading;
        },
        routeParam() {
            return this.$route.params.id;
        },
        popupKey() {
            return this.$store.getters["client/popupKey"];
        },
        form_dirty() {
            //TODO : check if need this for estimate page
            return this.$store.getters["billing/isDirty"];
        },
        isBusinessLoading() {
            return this.$store.getters["isBusinessLoading"];
        },
        numericTotalAmount() {
            if (this.estimateDetails && this.estimateDetails.total_amount) {
                const cleanedAmount = String(
                    this.estimateDetails.total_amount
                ).replace(/[^0-9.]/g, "");
                return parseFloat(cleanedAmount);
            }
            return 0;
        }
    },
    watch: {
        routeParam() {
            this.$store.commit("billing/setEstimateDetails", null);
            this.reloadPageDetails();
            this.cardKey++;
        },
        isBusinessLoading() {
            if (!this.isBusinessLoading && !this.isDataLoaded) {
                this.$emit("redirect-to-dashboard");
            }
        }
    },
    mounted() {
        let self = this;
        // fetch uom
        this.$store.dispatch("references/getUOM");
        this.reloadPageDetails();

        this.$root.$on("refresh-estimate-page", (reloadTab = null) => {
            this.reloadPage(reloadTab);
        });
    },
    destroyed() {
        this.$root.$off("refresh-estimate-page");
        this.$store.dispatch("billing/clearEstimateDetails");
    },
    methods: {
        async reloadPageDetails() {
            await this.getDetails();
        },
        async storeAudit() {
            const estimateId = _.clone(this.estimateId);
            const fullPath = _.clone(this.$router.currentRoute.fullPath);

            if (!estimateId) {
                return;
            }

            await this._getSetting();
            await this._saveAudit({
                action: "view",
                path: "/billing/estimate/view." + estimateId,
                reference_id: estimateId,
                fullPath
            });
        },
        /////////////////
        // FLOW SAVING //
        /////////////////
        /**
         * @method savingCheckFormValid
         * @description called from item/index.vue and then call function checkFormIsValid in item/create-update.vue
         * @return {Promise}
         */
        savingCheckFormValid() {
            return this.$refs.estimateEdit.checkFormIsValid();
        },
        /**
         * @method savingEditItem
         * @description called from item/index.vue and then call function proceedSave in item/create-update.vue with
         * boolean isUpdateUses
         * @return {void}
         */
        savingEditInvoice() {
            this.$refs.estimateEdit.proceedSave();
        },
        /**
         * @method saved
         * @description called from item/create-update.vue means the item has been saved
         * @return {void}
         */
        saved(result) {
            // TODO : update to estimate
            this.$emit("saved", result);
            this.reloadPageDetails();
            if (result.isUpdateUses) {
                this.reloadTabList("tabUses");
            } else {
                this.resetTab();
            }
        },
        /**
         * @method resetTab
         * @description To reset the tab to price tier or the first tab each time open edit/cancel edit and after edit
         * WIP: will make this dynamic by accept index to open specific tab
         */
        resetTab() {
            this.keyComponent++;
            this.activeTabIndex = 0;
        },
        async getDetails() {
            let self = this;
            self.isDataReloading = true;
            const encodedId = this.$route.params.id;
            const index = this.$route.params.index;

            if (encodedId || index) {
                // this.$store.commit("billing/setDirty", false);
                await this.$store
                    .dispatch("billing/getEstimateDetails", {
                        estimateId: index || encodedId,
                        index: index ? 1 : 0
                    })
                    .then(res => {
                        if (res && res.message && res.message.code == 200) {
                            let estName = "Estimate #" + res.resource.index;
                            self.dynamicPageTitle(estName);
                            CBILLINGS.INVOICE.TYPE_CLOSED;

                            // res.resource.isBusinessActorActive =
                            //     res.resource.client &&
                            //     res.resource.client.single_business_actor &&
                            //     res.resource.client.single_business_actor
                            //         .isactive;

                            self.storeAudit();
                            // self.$emit("set-invoice-details", res.resource);
                            self.isDataReloading = false;
                            self.firstTimeLoaded = true;
                            if (res.resource && res.resource.client) {
                                this.reloadPopupNotes();
                            }
                        } else if (!this.isBusinessLoading) {
                            if (res.switch_business) {
                                //switch clinic/business when checking in PreventRestrictedUser middleware
                                this.$refs.modalSwitchBusiness.open(res);
                            } else if (res.no_access) {
                                //no access when checking in PreventRestrictedUser middleware
                                self._message(res.message, "danger");
                                self.redirectToDashboard();
                            } else {
                                self.redirectToDashboard();
                            }
                        }
                    });
            } else {
                self.redirectToDashboard();
            }
        },
        reloadPopupNotes() {
            this.$store.commit("client/clearPopupNotes"); // clear popup notes by default

            this.$store.dispatch(
                "client/loadPopupNotes",
                this.estimateDetails.client.id
            );
        },
        reloadTabList(tabName) {
            this.activeTabIndex = this.findTabIndex(tabName);
            this.keyComponent++;
        },
        reloadPage(tabName = "tabItems") {
            this.reloadPageDetails();
            if (tabName) {
                this.reloadTabList(tabName);
            }
        },
        createBundle() {
            this.$refs.modalBundle.open();
        },

        findTabIndex(tabname) {
            return findIndex(this.$refs.btabs.$children, function (tab) {
                return tab.$attrs.tabname == tabname;
            });
        },
        addEstimateItem() {
            this.$refs["estimateItemModal"].open();
        },
        redirectToDashboard() {
            this.$emit("redirect-to-dashboard");
        }
    }
};
</script>

<style scoped>
.preloader {
    margin-left: 15px;
}
</style>
